import { useEffect, useState } from 'react'
import {
  AdminsApi,
  AdminsEcommerceCreateProductCategoryRequest,
  AdminsEcommerceGetProductCategoryItem,
  AdminsEcommerceGetProductCategoryRolePointUsageItem,
  AdminsEcommerceUpdateProductCategoryRequest,
  Configuration,
  UserRoleEnum,
} from 'generated/api'
import { axiosDefaultConfig } from 'constants/axios'
import Toast from 'components/toast/Toast'

const axiosConfig = new Configuration(axiosDefaultConfig)
const adminsApi = new AdminsApi(axiosConfig)

export const useCategories = () => {
  const { toast, toastContainer } = Toast()
  const [fetchStatusCategories, setFetchStatusCategories] = useState<'idle' | 'loading' | 'failed'>(
    'idle',
  )
  const [categories, setCategories] = useState<AdminsEcommerceGetProductCategoryItem[]>([])
  const [pageCategories, setPageCategories] = useState(1)
  const [totalRowsCategories, setTotalRowsCategories] = useState(0)
  const [limitPageCategories, setLimitPageCategories] = useState(10)
  const [searchKeywordCategories, setSearchKeywordCategories] = useState('')

  const [isModalOpenFormCategories, setIsModalOpenFormCategories] = useState(false)
  const [categoryId, setCategoryId] = useState('')
  const [submitStatusFormCategories, setSubmitStatusFormCategories] = useState<'idle' | 'loading'>(
    'idle',
  )
  const [initFormCategory, setInitFormCategory] =
    useState<AdminsEcommerceCreateProductCategoryRequest>({
      maxPointUsage: 0,
      name: '',
      rolePointUsageItems: [
        {
          role: UserRoleEnum.Agent,
          maxPointUsage: 0,
        },
        {
          role: UserRoleEnum.FieldCoordinator,
          maxPointUsage: 0,
        },
        {
          role: UserRoleEnum.Driver,
          maxPointUsage: 0,
        },
        {
          role: UserRoleEnum.Farmer,
          maxPointUsage: 0,
        },
        {
          role: UserRoleEnum.Harvester,
          maxPointUsage: 0,
        },
      ],
      status: 'ACTIVE',
    })

  useEffect(() => {
    fetchCategories()
  }, [pageCategories, limitPageCategories])

  useEffect(() => {
    if (!isModalOpenFormCategories) {
      setInitFormCategory({
        maxPointUsage: 0,
        name: '',
        rolePointUsageItems: [
          {
            role: UserRoleEnum.Agent,
            maxPointUsage: 0,
          },
          {
            role: UserRoleEnum.FieldCoordinator,
            maxPointUsage: 0,
          },
          {
            role: UserRoleEnum.Driver,
            maxPointUsage: 0,
          },
          {
            role: UserRoleEnum.Farmer,
            maxPointUsage: 0,
          },
          {
            role: UserRoleEnum.Harvester,
            maxPointUsage: 0,
          },
        ],
        status: 'ACTIVE',
      })
      setCategoryId('')
    }
  }, [isModalOpenFormCategories])

  const getPointByRole = (
    role: UserRoleEnum,
    rolePointUsageItems: AdminsEcommerceGetProductCategoryRolePointUsageItem[],
  ) => {
    return rolePointUsageItems.find(item => item.role === role)?.maxPointUsage || 0
  }

  const fetchCategories = async () => {
    try {
      setFetchStatusCategories('loading')
      const { data } = await adminsApi.adminsEcommerceGetProductCategory(
        '',
        pageCategories,
        limitPageCategories,
        'ACTIVE',
        searchKeywordCategories ? searchKeywordCategories : undefined,
      )
      setCategories(data.items)
      setTotalRowsCategories(data.pagination?.total || 0)
      setFetchStatusCategories('idle')
    } catch {
      setFetchStatusCategories('failed')
    }
  }

  const searchCategoriesByKeyword = () => {
    pageCategories === 1 ? fetchCategories() : setPageCategories(1)
  }

  const changePageCategories = (page: number) => {
    setPageCategories(page)
  }

  const changeLimitPageCategories = (limit: number) => {
    setLimitPageCategories(limit)
    setPageCategories(1)
  }

  const changeCategroiesKeyword = (keyword: string) => {
    setSearchKeywordCategories(keyword)
  }

  const openModalForm = (category?: AdminsEcommerceGetProductCategoryItem) => {
    setIsModalOpenFormCategories(true)
    if (category) {
      setInitFormCategory({
        maxPointUsage: category.maxPointUsage,
        name: category.name,
        rolePointUsageItems: [
          {
            role: UserRoleEnum.Agent,
            maxPointUsage: getPointByRole(UserRoleEnum.Agent, category.rolePointUsageItems),
          },
          {
            role: UserRoleEnum.FieldCoordinator,
            maxPointUsage: getPointByRole(
              UserRoleEnum.FieldCoordinator,
              category.rolePointUsageItems,
            ),
          },
          {
            role: UserRoleEnum.Driver,
            maxPointUsage: getPointByRole(UserRoleEnum.Driver, category.rolePointUsageItems),
          },
          {
            role: UserRoleEnum.Farmer,
            maxPointUsage: getPointByRole(UserRoleEnum.Farmer, category.rolePointUsageItems),
          },
          {
            role: UserRoleEnum.Harvester,
            maxPointUsage: getPointByRole(UserRoleEnum.Harvester, category.rolePointUsageItems),
          },
        ],
        status: 'ACTIVE',
      })
      setCategoryId(category.id)
    }
  }

  const closeModalForm = () => {
    setIsModalOpenFormCategories(false)
  }

  const submitCategory = async (
    payload:
      | AdminsEcommerceCreateProductCategoryRequest
      | AdminsEcommerceUpdateProductCategoryRequest,
  ) => {
    if (categoryId) {
      await updateCategory(payload as AdminsEcommerceUpdateProductCategoryRequest)
    } else {
      await createCategory(payload)
    }
  }

  const createCategory = async (payload: AdminsEcommerceCreateProductCategoryRequest) => {
    try {
      setSubmitStatusFormCategories('loading')
      await adminsApi.adminsEcommerceCreateProductCategory('', payload)
      setSubmitStatusFormCategories('idle')
      await afterActionCategory('create')
    } catch {
      setSubmitStatusFormCategories('idle')
      await toast('Gagal membuat kategori', 'danger')
    }
  }

  const updateCategory = async (payload: AdminsEcommerceUpdateProductCategoryRequest) => {
    try {
      setSubmitStatusFormCategories('loading')
      await adminsApi.adminsEcommerceUpdateProductCategory(categoryId, payload)
      setSubmitStatusFormCategories('idle')
      await afterActionCategory('update')
    } catch {
      setSubmitStatusFormCategories('idle')
      await toast('Gagal memperbarui kategori', 'danger')
    }
  }

  const afterActionCategory = async (action: 'create' | 'update') => {
    if (['create', 'update'].includes(action)) {
      const message =
        action === 'create' ? 'Berhasil membuat kategori' : 'Berhasil memperbarui kategori'
      closeModalForm()
      await toast(message, 'success')
      await fetchCategories()
    }
  }

  return {
    fetchStatusCategories,
    categories,
    pageCategories,
    limitPageCategories,
    totalRowsCategories,
    searchKeywordCategories,
    fetchCategories,
    changePageCategories,
    changeLimitPageCategories,
    changeCategroiesKeyword,
    searchCategoriesByKeyword,
    isModalOpenFormCategories,
    openModalForm,
    closeModalForm,
    submitCategory,
    initFormCategory,
    categoryId,
    submitStatusFormCategories,
    toastContainer,
  }
}
